import { CostOptimizationHandler } from './CostOptimizationHandler';
import { AuthorizationUtil, PlatformAuthorizer, PlatformResponse } from '@quickplay/platform-authorizer';
import { createRequestBuilder, HttpRequest, Logger } from '@quickplay/foundation';

type CostOptimizationRequest = { [key: string]: string };

type CostOptimizationResponse = {
  qoe: any;
};

export class CostOptimizationHandlerImpl implements CostOptimizationHandler {
  private logger?: Logger;
  private refreshInterval: number;
  private liveThreshold: number;
  private vodThreshold: number;
  private refreshIntervalRef: any;

  constructor() {
    this.refreshInterval = 10;
    this.liveThreshold = 0;
    this.vodThreshold = 0;
  }

  get liveThresholdMs(): number {
    return this.liveThreshold * 1_000;
  }

  get vodThresholdMs(): number {
    return this.vodThreshold * 1_000;
  }

  initialize(
    url: string,
    params: { [key: string]: any },
    platform: PlatformAuthorizer,
    refreshInterval?: number,
    logger?: Logger,
  ): Promise<void> {
    this.logger = logger;

    if (refreshInterval) {
      this.refreshInterval = refreshInterval;
    }

    const request = createRequestBuilder<CostOptimizationRequest>()
      .url(url)
      .path('/device/throttle/lookup')
      .post(params)
      .headers({ accept: 'application/json' })
      .build();

    return new Promise((resolve, reject) => {
      this.getCostOptimizationData(request, platform)
        .then(() => {
          this.refreshIntervalRef = setInterval(
            async () => {
              await this.getCostOptimizationData(request, platform);
            },
            this.refreshInterval * 60 * 1_000,
          );

          resolve();
        })
        .catch((error: any) => {
          reject(error);
        });
    });
  }

  private getCostOptimizationData(
    request: HttpRequest<CostOptimizationRequest>,
    platform: PlatformAuthorizer,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      AuthorizationUtil.makeAuthorizedRequest<CostOptimizationRequest, CostOptimizationResponse>(request, platform)
        .then((response: PlatformResponse<CostOptimizationResponse>) => {
          if (!response.data?.qoe?.enable) {
            this.logger?.log('[ANALYTICS][COST_OPTIMIZATION] disabled');

            resolve();
            return;
          }

          this.liveThreshold = response.data.qoe.threshold.live;
          this.vodThreshold = response.data.qoe.threshold.vod;

          this.logger?.log(
            `[ANALYTICS][COST_OPTIMIZATION] refreshed [LIVE: ${this.liveThreshold}] [VOD: ${this.vodThreshold}]`,
          );

          resolve();
        })
        .catch((error: any) => {
          this.logger?.log('[ANALYTICS][COST_OPTIMIZATION] refresh failure', error);
          reject(error);
        });
    });
  }

  release(): void {
    if (this.refreshIntervalRef) {
      clearInterval(this.refreshIntervalRef);
      this.refreshIntervalRef = null;
      this.logger?.log('[ANALYTICS][COST_OPTIMIZATION] released');
    }
  }
}
